import { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

const CopyField = ({ label, value, fieldKey, copiedKey, onCopy }) => {
    const copied = copiedKey === fieldKey;

    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-secondary uppercase tracking-wide">{label}</span>
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-bgAccent border border-border">
                <span className="flex-1 text-sm font-mono text-font-primary truncate select-all">{value}</span>
                <button
                    onClick={() => onCopy(fieldKey, value)}
                    className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200 button-container"
                    title={copied ? "Copied!" : `Copy ${label}`}
                >
                    <Icon
                        icon={copied ? "mdi:check" : "mdi:content-copy"}
                        className={`text-sm transition-colors ${copied ? "text-green-400" : "text-font-secondary"}`}
                    />
                    <span className={`transition-colors ${copied ? "text-green-400" : "text-font-secondary"}`}>
                        {copied ? "Copied" : "Copy"}
                    </span>
                </button>
            </div>
        </div>
    );
};

const UserCreatedModal = ({ isOpen, onClose, inviteLink, tempPassword, userName }) => {
    const [copiedKey, setCopiedKey] = useState(null);

    const handleCopy = (key, value) => {
        navigator.clipboard.writeText(value).then(() => {
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 2000);
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-background rounded-xl w-full max-w-lg shadow-2xl border border-border flex flex-col">
                {/* Header */}
                <div className="flex items-center gap-3 p-5 border-b border-border">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-500 bg-opacity-15 flex-shrink-0">
                        <Icon icon="mdi:check-circle" className="text-2xl text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-bold text-font-primary">User Created Successfully</h2>
                        {userName && (
                            <p className="text-sm text-font-secondary truncate">
                                Account set up for <span className="text-font-primary font-medium">{userName}</span>
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg hover:bg-bgAccent transition-colors button-container"
                        title="Close"
                    >
                        <Icon icon="mdi:close" className="text-lg text-font-secondary" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 flex flex-col gap-5">
                    {/* Invite Link — Primary */}
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <Icon icon="mdi:link-variant" className="text-lg text-primary flex-shrink-0" />
                            <span className="font-semibold text-font-primary">Invite Link</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary bg-opacity-15 text-primary font-medium">Recommended</span>
                        </div>
                        <p className="text-sm text-font-secondary leading-relaxed">
                            Send this link to the user. They'll be prompted to set their own password.
                            <span className="text-yellow-400 font-medium"> Expires in 7 days.</span>
                        </p>
                        {inviteLink ? (
                            <CopyField
                                label="One-time password setup link"
                                value={inviteLink}
                                fieldKey="invite"
                                copiedKey={copiedKey}
                                onCopy={handleCopy}
                            />
                        ) : (
                            <p className="text-xs text-font-secondary italic">Invite link not available for this user.</p>
                        )}
                    </div>

                    <div className="border-t border-border" />

                    {/* Temp Password — Secondary */}
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <Icon icon="mdi:key-outline" className="text-lg text-font-secondary flex-shrink-0" />
                            <span className="font-semibold text-font-secondary">Temporary Password</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-bgAccent text-font-secondary font-medium">Fallback</span>
                        </div>
                        <p className="text-sm text-font-secondary leading-relaxed">
                            Use only if the invite link doesn't work. The user will be required to change this on first login.
                        </p>
                        {tempPassword ? (
                            <CopyField
                                label="Temporary password"
                                value={tempPassword}
                                fieldKey="password"
                                copiedKey={copiedKey}
                                onCopy={handleCopy}
                            />
                        ) : (
                            <p className="text-xs text-font-secondary italic">Temporary password not available.</p>
                        )}
                    </div>

                    {/* Security warning */}
                    <div className="flex items-start gap-2.5 p-3 rounded-lg border border-yellow-500 border-opacity-40 bg-yellow-500 bg-opacity-5">
                        <Icon icon="mdi:shield-alert-outline" className="text-lg text-yellow-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-font-secondary leading-relaxed">
                            <span className="text-yellow-400 font-semibold">Security notice:</span> Share the temporary password only through secure, private channels — not over email, Slack, or other public platforms.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-5 pt-0">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors button-container"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserCreatedModal;
